import React, { useState, useEffect } from 'react';
import './Resources.css';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { WorkshopInformationSessionsData } from '../data/eventsData';

function Resources() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const getYouTubeVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  }

  const getYouTubeThumbnail = (url, quality = 'maxresdefault') => {
    const videoId = getYouTubeVideoId(url);
    return videoId ? `https://img.youtube.com/vi/${videoId}/${quality}.jpg` : null;
  }

  // Check if user is logged in
  useEffect(() => {
    checkUser();
    
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkUser = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    } catch (error) {
      console.error('Error checking user:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.username || !formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      // First, check if username already exists
      const { data: usernameCheck, error: usernameError } = await supabase
        .from('user_profiles')
        .select('username')
        .eq('username', formData.username)
        .maybeSingle();

      // If username exists, show error
      if (usernameCheck && usernameCheck.username) {
        setError('Username already exists. Please choose a different username.');
        return;
      }

      // If there's an error checking username (other than "not found"), log it but continue
      // We'll let the database unique constraint catch it if the policy doesn't work
      if (usernameError && usernameError.code !== 'PGRST116') {
        console.error('Error checking username:', usernameError);
        // Continue anyway - the unique constraint will catch duplicates
      }

      // Sign up with email and password
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
      });

      // Check for email already exists error
      if (authError) {
        if (authError.message.includes('already registered') || 
            authError.message.includes('already exists') ||
            authError.message.includes('User already registered')) {
          setError('Account already exists with this email. Please sign in instead.');
          return;
        }
        throw authError;
      }

      // Store username in user metadata
      if (authData.user) {
        const { error: updateError } = await supabase.auth.updateUser({
          data: { username: formData.username }
        });

        if (updateError) {
          console.error('Error updating user metadata:', updateError);
        }

        // Also store in a custom table if needed
        const { error: dbError } = await supabase
          .from('user_profiles')
          .insert([
            { 
              id: authData.user.id, 
              username: formData.username,
              email: formData.email 
            }
          ]);

        // Handle database errors
        if (dbError) {
          if (dbError.code === '23505') {
            // Unique constraint violation - username already exists
            if (dbError.message.includes('username') || dbError.message.includes('user_profiles_username_key')) {
              setError('Username already exists. Please choose a different username.');
              setFormData({ ...formData, username: '' }); // Clear username field
              return;
            } else if (dbError.message.includes('email')) {
              setError('Account already exists with this email. Please sign in instead.');
              return;
            } else {
              setError('This username is already taken. Please choose a different username.');
              setFormData({ ...formData, username: '' }); // Clear username field
              return;
            }
          }
          console.error('Error saving to database:', dbError);
        }
      }

      setSuccess('Account created successfully! A confirmation email has been sent to your email address. Please check your inbox and click the confirmation link to verify your account before logging in.');
      setFormData({ username: '', email: '', password: '' });
    } catch (error) {
      // Handle specific error messages
      if (error.message.includes('already registered') || 
          error.message.includes('already exists') ||
          error.message.includes('User already registered')) {
        setError('Account already exists with this email. Please sign in instead.');
      } else if (error.message.includes('username') && error.message.includes('exists')) {
        setError('Username already exists. Please choose a different username.');
      } else {
        setError(error.message || 'Failed to create account. Please try again.');
      }
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (signInError) throw signInError;

      setUser(data.user);
      setFormData({ username: '', email: '', password: '' });
    } catch (error) {
      setError(error.message || 'Invalid email or password. Please try again.');
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
    } catch (error) {
      setError('Failed to sign out. Please try again.');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(formData.email, {
        redirectTo: `${window.location.origin}/forget-password`,
      });

      if (resetError) throw resetError;

      setSuccess('Password reset email sent! Please check your email inbox and follow the instructions to reset your password.');
      setFormData({ ...formData, email: '' });
      setShowForgotPassword(false);
    } catch (error) {
      setError(error.message || 'Failed to send password reset email. Please try again.');
    }
  };

  // Show configuration error if Supabase is not configured
  if (!isSupabaseConfigured) {
    return (
      <div className="resources__container">
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-header">
              <h1 className="auth-title">Configuration Required</h1>
              <p className="auth-subtitle">Supabase credentials are not configured</p>
            </div>
            <div className="auth-config-message">
              <p>To enable authentication, please:</p>
              <ol>
                <li>Create a <code>.env</code> file in the <code>client</code> directory</li>
                <li>Add your Supabase credentials:</li>
              </ol>
              <pre className="auth-code-block">
{`REACT_APP_SUPABASE_URL=your_supabase_project_url
REACT_APP_SUPABASE_ANON_KEY=your_supabase_anon_key`}
              </pre>
              <p>See <code>AUTH_SETUP.md</code> for detailed instructions.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div className="resources__container">
        <div className="auth-loading">
          <div className="auth-loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Show login/signup form if not authenticated
  if (!user) {
    return (
      <div className="resources__container">
        <div className="auth-container">
          <div className="auth-intro">
            <h1 className="auth-intro-title">YWEP Resources</h1>
            <p className="auth-intro-desc">
              YWEP Resources is a sign-in–only section featuring materials developed and compiled as part of the Young Women in Economics Program (YWEP) and its scale-up initiative. Access is limited to registered program participants.
            </p>
          </div>
          <div className="auth-card">
            <div className="auth-header">
              <h1 className="auth-title">
                {showForgotPassword 
                  ? 'Reset Password' 
                  : isLoginMode 
                    ? 'Welcome Back' 
                    : 'Create Account'}
              </h1>
              <p className="auth-subtitle">
                {showForgotPassword
                  ? 'Enter your email to receive a password reset link'
                  : isLoginMode 
                    ? 'Sign in to access resources' 
                    : 'Sign up to access resources'}
              </p>
            </div>

            {showForgotPassword ? (
              <form 
                className="auth-form" 
                onSubmit={handleForgotPassword}
              >
                <div className="auth-form-group">
                  <label htmlFor="reset-email" className="auth-label">Email</label>
                  <input
                    type="email"
                    id="reset-email"
                    name="email"
                    className="auth-input"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                {error && <div className="auth-error">{error}</div>}
                {success && <div className="auth-success">{success}</div>}

                <button type="submit" className="auth-submit-btn">
                  Send Reset Link
                </button>

                <div className="auth-forgot-back">
                  <button 
                    type="button"
                    className="auth-switch-btn"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setError('');
                      setSuccess('');
                      setFormData({ ...formData, email: '' });
                    }}
                  >
                    ← Back to Sign In
                  </button>
                </div>
              </form>
            ) : (
              <form 
                className="auth-form" 
                onSubmit={isLoginMode ? handleSignIn : handleSignUp}
              >
                {!isLoginMode && (
                  <div className="auth-form-group">
                    <label htmlFor="username" className="auth-label">Username</label>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      className="auth-input"
                      placeholder="Enter your username"
                      value={formData.username}
                      onChange={handleInputChange}
                      required={!isLoginMode}
                    />
                  </div>
                )}

                <div className="auth-form-group">
                  <label htmlFor="email" className="auth-label">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="auth-input"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="auth-form-group">
                  <label htmlFor="password" className="auth-label">Password</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    className="auth-input"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                {isLoginMode && (
                  <div className="auth-forgot-link">
                    <button
                      type="button"
                      className="auth-forgot-btn"
                      onClick={() => {
                        setShowForgotPassword(true);
                        setError('');
                        setSuccess('');
                        setFormData({ ...formData, password: '' });
                      }}
                    >
                      Forgot Password?
                    </button>
                  </div>
                )}

                {error && <div className="auth-error">{error}</div>}
                {success && <div className="auth-success">{success}</div>}

                <button type="submit" className="auth-submit-btn">
                  {isLoginMode ? 'Sign In' : 'Sign Up'}
                </button>
              </form>
            )}

            {!showForgotPassword && (
              <div className="auth-switch">
                <p>
                  {isLoginMode ? "Don't have an account? " : 'Already have an account? '}
                  <button 
                    className="auth-switch-btn"
                    onClick={() => {
                      setIsLoginMode(!isLoginMode);
                      setError('');
                      setSuccess('');
                      setFormData({ username: '', email: '', password: '' });
                    }}
                  >
                    {isLoginMode ? 'Sign Up' : 'Sign In'}
                  </button>
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Public resources (no sign-in required) */}
        <section className="resources-public">
          <div className="resources-public-inner">
            <h2 className="resources-public-heading">Data Resources</h2>
            <p className="resources-public-sub">Documents and links to get started with learning R, Python, and Stata.</p>
            <ul className="resources-public-list">
              <li>
                <a href="https://docs.google.com/document/d/1BglPeHVqbSAOdW6LGN-Vl1IBsHXbUwRFqOvqqM-678Q/edit" target="_blank" rel="noopener noreferrer">
                  Get started with R
                </a>
                <span className="resources-public-meta"> (Compiled by Saumya Singla)</span>
              </li>
              <li>
                <a href="https://www.dropbox.com/sh/e0oe231g1w0lz8a/AAA3PyTHJn7rhF001Uxd-zW5a?dl=0" target="_blank" rel="noopener noreferrer">
                  Get started with Python
                </a>
                <span className="resources-public-meta"> (Compiled by Torsha Chakravorty)</span>
              </li>
              <li>
                <a href="https://docs.google.com/document/d/1sA7oo3an_OgFPKEnH8EYl8l5UsOTWs1_zSjjXDwqxL8/edit" target="_blank" rel="noopener noreferrer">
                  Get started with STATA
                </a>
                <span className="resources-public-meta"> (Compiled by Prashansa Srivastava)</span>
              </li>
            </ul>

            <h2 className="resources-public-heading">Career Resources</h2>
            <p className="resources-public-sub">Websites, preparation resources, and newsletters for jobs and internships in economics and policy.</p>

            <h3 className="resources-public-subhead">Websites</h3>
            <ul className="resources-public-list">
              <li>
                <a href="https://www.devnetjobsindia.org/" target="_blank" rel="noopener noreferrer">DevNetJobsIndia</a>
                <span className="resources-public-meta"> — Development jobs and consulting opportunities in India</span>
              </li>
              <li>
                <a href="https://unjobs.org/" target="_blank" rel="noopener noreferrer">UNJobs</a>
                <span className="resources-public-meta"> — Vacancies with United Nations and other international organizations</span>
              </li>
              <li>
                <a href="https://aadityadar.github.io/econ_ra_india/" target="_blank" rel="noopener noreferrer">Econ RA India</a>
                <span className="resources-public-meta"> — Resources for economics research assistants in India (Prof. Aaditya Dhar)</span>
              </li>
              <li>
                <a href="https://sites.google.com/view/econrin/home" target="_blank" rel="noopener noreferrer">EconRIN</a>
                <span className="resources-public-meta"> — A complete guide to economics research internship in India</span>
              </li>
            </ul>

            <h3 className="resources-public-subhead">Preparation Resources</h3>
            <ul className="resources-public-list">
              <li>
                <a href="https://www.povertyactionlab.org/research-resources?view=toc" target="_blank" rel="noopener noreferrer">J-PAL Research Resources</a>
                <span className="resources-public-meta"> — Good starting point for learning about Randomized Controlled Trials (RCTs); useful for JPAL, EPoD, IFMR, etc.</span>
              </li>
              <li>
                <a href="http://runningres.com/" target="_blank" rel="noopener noreferrer">Running Randomized Evaluations (RRE)</a>
                <span className="resources-public-meta"> — Technical aspects of RCTs; useful for technical rounds</span>
              </li>
            </ul>

            <h3 className="resources-public-subhead">Newsletters</h3>
            <ul className="resources-public-list">
              <li>
                <a href="https://gmail.us17.list-manage.com/subscribe?u=3d315482a01ddd29e7b7161ac&id=04e8ae4d84" target="_blank" rel="noopener noreferrer">Women in Econ/Policy</a>
                <span className="resources-public-meta"> — Fortnightly newsletter with job postings and readings on development (subscribe here)</span>
              </li>
              <li>
                <a href="https://publicpolicyindia.com/the-policy-post-newsletter/" target="_blank" rel="noopener noreferrer">The Policy Post (Public Policy India)</a>
                <span className="resources-public-meta"> — Weekly newsletter with job and internship opportunities in public policy and development</span>
              </li>
              <li>
                <a href="https://policypeople.substack.com/" target="_blank" rel="noopener noreferrer">Policy People (Liam Gibson)</a>
                <span className="resources-public-meta"> — Curated think tank jobs and events, delivered twice a week</span>
              </li>
            </ul>
          </div>
        </section>
      </div>
    );
  }

  const resources = [
    // Network/Community
    {
      category: 'Network/Community',
      name: 'Mentee Orientation',
      type: 'Orientation',
      resourcePerson: '',
      links: [
        'https://drive.google.com/drive/folders/1o09D6rd_sLHll8o9APWfRVq-M-qtqWFy?usp=sharing',
        'https://drive.google.com/file/d/1dBCzsug7MKJvZbdSKFd7czESz9jlD_dm/view?usp=sharing'
      ],
      additionalInfo: 'CV Resources and templates',
      micrositeLink: 'https://sites.google.com/d/1207BlDDs-Px_HsSASqTUODdcHRdNzKng/p/1RUBvcRhragqrYUOf6tO8oDxdXUa1YWKc/edit'
    },
    {
      category: 'Network/Community',
      name: 'Mentor Orientation',
      type: 'Orientation',
      resourcePerson: '',
      links: [],
      additionalInfo: "Master's Prep Resources, Scholarships and Fellowships",
      micrositeLink: 'https://sites.google.com/d/1xRh8AhquPYd5zvcwefjLFjv-Fi2H0KoA/p/16TFg20SXuvAu3GRaY_mo3CKwOH7kvYpe/edit'
    },
    {
      category: 'Network/Community',
      name: 'Mentor Training (Caste + LGBTQIA + State Uni Issues)',
      type: 'Orientation',
      resourcePerson: '',
      links: [],
      additionalInfo: 'STATA Resources and Textbooks'
    },
    {
      category: 'Network/Community',
      name: 'Mentee Townhalls',
      type: 'Orientation',
      resourcePerson: '',
      links: []
    },
    {
      category: 'Network/Community',
      name: 'Mental Health',
      type: 'General Sessions',
      resourcePerson: 'Manveen Kaur',
      links: []
    },
    
    // Soft Skills
    {
      category: 'Soft Skills',
      name: 'Network Building + Communication Skills',
      type: 'General Sessions',
      resourcePerson: 'Radhika K. and/or Saumya S.',
      links: ['https://drive.google.com/file/d/1hhNVoOg7j3oXHh3oVj5WMz6ymjdCa5R5/view']
    },
    {
      category: 'Soft Skills',
      name: 'CV Building and Writing',
      type: 'General Sessions',
      resourcePerson: 'Sakshi H. and Salonie C.',
      links: ['https://drive.google.com/file/d/10MqfKM0DOIIjCFkiNg_O7aOjtDhShPRT/view']
    },
    {
      category: 'Soft Skills',
      name: 'Development Jobs: CV Rundown',
      type: 'Specific Sessions: Development Jobs',
      resourcePerson: 'Sakshi H., Fizza S., Kaveri S.',
      links: ['https://drive.google.com/file/d/1XHZZ2P3uJKo3WiZtAAVlt9XZKhuK4gxN/view']
    },
    {
      category: 'Soft Skills',
      name: 'Corporate Jobs: CV Rundown',
      type: 'Specific Sessions: Corporate',
      resourcePerson: 'Salonie C.',
      links: ['https://drive.google.com/file/d/1fZVn-w3488ErkiusgekdMexNm6sIDMnG/view']
    },
    {
      category: 'Soft Skills',
      name: 'Corporate Jobs: Cover Letter',
      type: 'Specific Sessions: Corporate',
      resourcePerson: 'Kaveri S., Ahana R.',
      links: ['https://drive.google.com/file/d/1FK4nNwoP9m6lZp_AOyihjTJMFNs-qOM2/view?usp=sharing']
    },
    {
      category: 'Soft Skills',
      name: 'Development Jobs: Cover Letter',
      type: 'Specific Sessions: Development Jobs',
      resourcePerson: 'Anahita K., Ridhi A.',
      links: ['https://drive.google.com/file/d/1q16MNOpoWN6yLQU5q4aKswmNCNapG1sG/view?usp=sharing']
    },
    {
      category: 'Soft Skills',
      name: 'Cover Letter Writing',
      type: 'General Sessions',
      resourcePerson: 'Anahita K. and Sakshi H.',
      links: ['https://drive.google.com/file/d/1h2IRZ5okXG6W7xuEDFnEZKAxParQWAPE/view']
    },
    {
      category: 'Soft Skills',
      name: 'Development Jobs: Interviews',
      type: 'Specific Sessions: Development Jobs',
      resourcePerson: 'Maitri P., Annapurna M.',
      links: [
        'https://drive.google.com/file/d/1dZY1VFdsU6RfKVLglXlJsXA278Uk7hP6/view?usp=sharing',
        'https://drive.google.com/file/d/1Kw0fGpkQlPyYrMp4PfLX4sZCwjKjagAE/view?usp=sharing'
      ]
    },
    {
      category: 'Soft Skills',
      name: 'How to Present Yourself in an Interview + Tips for HR Round',
      type: 'General Sessions',
      resourcePerson: 'Salonie C.',
      links: ['https://drive.google.com/file/d/1Jdhq5qL5C4m0VmJ7pmn9_t1BI0ZihHtO/view']
    },
    {
      category: 'Soft Skills',
      name: 'Corporate and Dev Consulting Jobs: Interviews + Case Prep',
      type: 'Specific Sessions: Corporate',
      resourcePerson: 'Kaveri S., Aanchal G.',
      links: []
    },
    {
      category: 'Soft Skills',
      name: 'Negotiation Skills',
      type: 'General Sessions',
      resourcePerson: 'Rachel M. G.',
      links: []
    },

    // Sector Knowledge
    {
      category: 'Sector Knowledge',
      name: 'Finding a Pre-Doc/RA Position',
      type: 'Specific Sessions: Development Jobs',
      resourcePerson: 'Anahita K., Radhika K., Samhita K.',
      links: ['https://drive.google.com/file/d/1xTqS24lzAT1oZw9zW8MD0lWEjf5687rU/view']
    },
    {
      category: 'Sector Knowledge',
      name: 'Applying for MA/MPP Courses in India or Abroad',
      type: 'Specific Sessions: PhD/MA/MPhil',
      resourcePerson: 'Nikita S., Yutika V., Smriti S.',
      links: ['https://drive.google.com/file/d/1HRymTSy0dYlPYD3OIYofrYE3DmaUNclG/view']
    },
    {
      category: 'Sector Knowledge',
      name: 'SOP Writing, Diversity Statement, LORs',
      type: 'Specific Sessions: PhD/MA/MPhil',
      resourcePerson: 'Prerna K., Torsha C.',
      links: []
    },
    {
      category: 'Sector Knowledge',
      name: 'Applying to PhD in India and Abroad',
      type: 'Specific Sessions: PhD/MA/MPhil',
      resourcePerson: 'Prerna K., Vatsala S., Pranusha K., Saheli B.',
      links: []
    },
    {
      category: 'Sector Knowledge',
      name: 'Non-Academic Jobs with Econ Degree: Think Tanks, NGO Careers, Journalism',
      type: 'Specific Sessions: Development Jobs',
      resourcePerson: 'Ria K., Tanushree C., Shaily J., Sharanya',
      links: ['https://drive.google.com/file/d/1dFaB5g-yMkG6SPZ87czlT3sx1ex852Zy/view?usp=sharing']
    },
    {
      category: 'Sector Knowledge',
      name: 'International Organization Careers',
      type: 'General Sessions',
      resourcePerson: 'Annapurna M., Shriya C.',
      links: ['https://drive.google.com/file/d/1LV8R1TtJiLO4JYv_NPv52nQwmt7hKTNY/view']
    },
    {
      category: 'Sector Knowledge',
      name: 'Tech, Policy and Consulting Jobs',
      type: 'Specific Sessions: Corporate',
      resourcePerson: '',
      links: [
        'https://drive.google.com/file/d/1L1n6fhInS4uLpROaAFZl20KHYIUNSe74/view?usp=sharing',
        'https://drive.google.com/file/d/1tQ9jA4bCyuqVia5RI7_lwgn0QKRsriAz/view?usp=sharing',
        'https://drive.google.com/file/d/1TBR2JcRQPBOVioighIOPRsbhDFwQkn1A/view'
      ]
    },
    {
      category: 'Sector Knowledge',
      name: 'Development Jobs: Government Jobs & Civil Services',
      type: 'Specific Sessions: Development Jobs',
      resourcePerson: 'Kaveri S. and Radha K.',
      links: ['https://drive.google.com/file/d/1TRDeNEptmPszy8r8uBDe76yHmST931Sa/view?usp=sharing']
    },
    {
      category: 'Sector Knowledge',
      name: 'Applying for External Scholarships/Funding for Graduate School',
      type: 'Specific Sessions: PhD/MA/MPhil',
      resourcePerson: 'Rai Sengupta',
      links: ['https://drive.google.com/file/d/1mpG0CgliJrUxzbfpoFUMjJ1nYO83sKDc/view?usp=sharing']
    },
    {
      category: 'Sector Knowledge',
      name: 'Non-Academic Jobs: Evidence-Driven Policy Research',
      type: 'Specific Sessions: Development Jobs',
      resourcePerson: 'Payal S, Lavanya G, Siobhan M.',
      links: ['https://drive.google.com/file/d/1WY1BRRt-41oTC1GgT5FxOl-uCe6F6yeW/view?usp=sharing']
    },
    {
      category: 'Sector Knowledge',
      name: 'Other Non-Academic Jobs: Consulting/Corporate World and Start-ups',
      type: 'General Sessions',
      resourcePerson: 'Michelle/Seerat, Yashaswini D, Shreya B.',
      links: ['https://drive.google.com/file/d/1TBR2JcRQPBOVioighIOPRsbhDFwQkn1A/view?usp=sharing']
    },

    // Technical Skills
    {
      category: 'Technical Skills',
      name: 'Development Jobs: Data Tasks and Skills',
      type: 'Specific Sessions: Development Jobs',
      resourcePerson: 'Smriti S., Vasanthi P.',
      links: []
    },
    {
      category: 'Technical Skills',
      name: 'Corporate Jobs: Data Tasks and Skills (Technical Test and Aptitude Exam Tips)',
      type: 'Specific Sessions: Corporate',
      resourcePerson: 'Saumya S. and Michelle N.',
      links: []
    },
    {
      category: 'Technical Skills',
      name: 'PhD Research Proposal/Writing Sample',
      type: 'Specific Sessions: PhD/MA/MPhil',
      resourcePerson: 'Maitri P.',
      links: [
        'https://drive.google.com/file/d/1zOe9CORd2lNt6MA5CDDz6A4diX-Y3X_O/view?usp=sharing',
        'https://drive.google.com/file/d/18dakXlmM_AaUaKaNd5fU0bpap3SMO-iM/view?usp=sharing'
      ]
    },
    {
      category: 'Technical Skills',
      name: 'Writing Workshop',
      type: 'Orientation',
      resourcePerson: '',
      links: []
    }
  ];

  const categories = ['all', 'Network/Community', 'Soft Skills', 'Sector Knowledge', 'Technical Skills'];

  const filteredResources = activeCategory === 'all' 
    ? resources 
    : resources.filter(r => r.category === activeCategory);

  // Get username from user metadata
  const username = user?.user_metadata?.username || user?.email?.split('@')[0] || 'User';

  return (
    <div className="resources__container">
      {/* User Info Bar */}
      <div className="resources-user-bar">
        <div className="resources-user-info">
          <span className="resources-user-greeting">Welcome, {username}!</span>
        </div>
        <button className="resources-logout-btn" onClick={handleSignOut}>
          Sign Out
        </button>
      </div>

      {/* Hero Section */}
      <section className="resources-hero">
        <div className="resources-hero-container">
          <h1 className="resources-hero-title">Resources & Sessions</h1>
          <p className="resources-hero-description">
            Access our comprehensive collection of sessions, guides, and materials from the Young Women in Economics Program (YWEP). 
            From CV building to PhD applications, interview prep to technical skills - find everything you need to advance your career in economics and policy.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="resources-filter-section">
        <div className="resources-filter-container">
          <h2 className="resources-filter-title">Browse by Category</h2>
          <div className="resources-filter-buttons">
            {categories.map(cat => (
              <button
                key={cat}
                className={`resources-filter-btn ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat === 'all' ? 'All Resources' : cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="resources-grid-section">
        <div className="resources-grid-container">
          <div className="resources-grid">
            {filteredResources.map((resource, index) => (
              <div key={index} className="resource-card">
                <div className="resource-card-header">
                  <span className="resource-category-badge">{resource.category}</span>
                  {resource.type && <span className="resource-type-badge">{resource.type}</span>}
                </div>
                <h3 className="resource-card-title">{resource.name}</h3>
            
                {resource.additionalInfo && (
                  <p className="resource-additional-info">{resource.additionalInfo}</p>
                )}
                {resource.links && resource.links.length > 0 && (
                  <div className="resource-links">
                    <p className="resource-links-title">Session Materials:</p>
                    {resource.links.map((link, idx) => (
                      <a 
                        key={idx} 
                        href={link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="resource-link-btn"
                      >
                        View Material {resource.links.length > 1 ? `${idx + 1}` : ''}
                      </a>
                    ))}
                  </div>
                )}
                {resource.micrositeLink && (
                  <a 
                    href={resource.micrositeLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="resource-microsite-link"
                  >
                    View Microsite →
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workshop & Information Sessions Section */}
      <section className="resources-workshop-section">
        <div className="WorkshopInformationSessions">
          <p className="WorkshopSessionsTitle">Workshop & Information Sessions</p>
          <p className="WorkshopSessionsDesc">Join us for interactive workshops and information sessions designed to help you navigate your career in economics and policy!</p>
          <div className="WorkshopSessionsGrid">
            {WorkshopInformationSessionsData.map((item, index) => (
              <a href={item.url} target="_blank" rel="noopener noreferrer" key={index}>
                <div className="WorkshopSessionItem">
                  <img
                    src={getYouTubeThumbnail(item.url)}
                    alt={item.title}
                    className="WorkshopSessionThumbnail"
                  />
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Resources;
